import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { find, findIndex, get, indexOf, isEmpty, isString, map } from 'lodash';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const DEFAULT_COLOR = 'black';
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'treemap-chart';

class TreemapChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = data => {
    const {
      alpha,
      colorRange,
      dataNamed,
      globalProperties,
      height,
      isCard,
      numericName,
      selectedElements,
      width
    } = this.props;

    const svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height])
      .style('overflow', 'visible');

    const hierarchy = d3
      .hierarchy(data)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    const treemap = d3
      .treemap()
      .size([width, height])
      .paddingInner(1)
      .round(true)(hierarchy);

    const leaf = svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('g')
      .data(treemap.leaves())
      .join('g')
      .attr('class', `${this.id}`)
      .attr('transform', d => `translate(${d.x0},${d.y0})`);

    const numericNamesAsObjects = map(numericName, name => ({ name }));
    leaf
      .append('rect')
      .attr('class', `${this.id}`)
      .attr('fill', d => {
        if (d.depth === 2) {
          const numericNameIndex = findIndex(numericNamesAsObjects, ['name', d.parent.data.name]);
          if (isString(colorRange)) return colorRange;
          if (isString(colorRange[numericNameIndex])) return colorRange[numericNameIndex];
          const dataNamedIndex = indexOf(dataNamed, d.data.name);
          return colorRange[numericNameIndex][dataNamedIndex];
        }
        return DEFAULT_COLOR;
      })
      .attr('fill-opacity', d => {
        if (d.depth === 2) {
          return alpha[findIndex(numericNamesAsObjects, ['name', d.parent.data.name])];
        }
        return 1;
      })
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0);

    if (!isCard) {
      leaf
        .append('title')
        .attr('class', `${this.id}`)
        .text(
          d =>
            `${d
              .ancestors()
              .reverse()
              .map(d2 => d2.data.name)
              .join('/')}\n${d.value}`
        );

      if (selectedElements.Text) {
        leaf
          .append('text')
          .attr('clip-path', d => d.clipUid)
          .attr('class', `${this.id}`)
          .selectAll('tspan')
          .data(d =>
            get(d, 'data.name', '')
              .split(/(?=[A-Z][a-z])|\s+/g)
              .concat(d.value)
          )
          .join('tspan')
          .attr('class', `${this.id}`)
          .attr(
            'font-size',
            get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE)
          )
          .attr('x', 3)
          .attr('y', (d, i, nodes) => `${(i === nodes.length - 1) * 0.3 + 1.1 + i * 0.9}em`)
          .attr('fill-opacity', (d, i, nodes) => (i === nodes.length - 1 ? 0.7 : null))
          .text(d => d);
      }
    }
  };

  getData = () => {
    const { dataNamed, dataNumeric, numericName } = this.props;

    let invalidAttribute = false;
    const data = {
      name: 'grafico',
      children: map(numericName, (namedValue, index) => ({
        name: namedValue,
        children: map(dataNumeric[index], (value, valueIndex) => {
          if (Number(value) < 0 && !invalidAttribute) invalidAttribute = true;
          return {
            name: dataNamed[valueIndex],
            value: Number(value)
          };
        })
      }))
    };

    return { data, invalidAttribute };
  };
}

TreemapChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C'],
  dataNumeric: [
    [1, 1.5, 2.2],
    [1.1, 1.7, 2.5],
    [1.2, 1.9, 2.8]
  ],
  colorRange: ['rgba(2,132,130)', 'rgb(122,186,122)', 'rgb(157,83,142)'],
  alpha: [0.9, 0.9, 0.9],
  numericName: ['AtributoA', 'AtributoB', 'AtributoC'],
  globalProperties: [],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  isCard: false
};
TreemapChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNumeric: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  ]),
  dataNamed: PropTypes.array,
  colorRange: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]))
  ]),
  alpha: PropTypes.arrayOf(PropTypes.number),
  globalProperties: PropTypes.array,
  selectedElements: PropTypes.object,
  numericName: PropTypes.arrayOf(PropTypes.string),
  isCard: PropTypes.bool
};

export default TreemapChart;
